import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import * as React from "react";
import { ensureFreshToken } from "../authProvider";
import DownloadField from "../unit/DownloadField";
import FileIconByExtension from "../unit/FileIconByExtension";
import formatDate from "./formatDate";
import useNotify from "./useNotify";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const buildUploadHeaders = async () => {
  await ensureFreshToken();

  const headers = new Headers({
    Accept: "application/json",
  });

  headers.set("reqDivision", localStorage.getItem("division") || "");
  headers.set("reqBank", localStorage.getItem("bank") || "");
  headers.set("reqAdmin", localStorage.getItem("permissions") || "");
  headers.set("reqType", "");
  headers.set(
    "Authorization",
    `Bearer ${localStorage.getItem("access_token") || ""}`
  );
  headers.set("X-XSRF-TOKEN", "");

  return headers;
};

const DocDeleteIcon = ({ ...props }) => {
  const { docId, docType, appId, onDeleted } = props;
  const reqUrl = "files/download/" + appId + "/" + docType + "";
  const { success, warning, error, info } = useNotify();

  const handDelete = async (docId: number, reqUrl: string) => {
    const reqDelUrl = `files/removeSpecialFiles/${docId}/${appId}`;
    const headers = await buildUploadHeaders();
    headers.set("Content-Type", "application/json");

    fetch(
      `${
        import.meta.env.VITE_API_URL
      }/${reqDelUrl}?fileType=${encodeURIComponent(docType || "")}`,
      {
        method: "DELETE",
        headers,
        body: JSON.stringify({
          fileType: docType,
        }),
      }
    )
      .then((response) => {
        if (response.ok) {
          success("File deleted successfully");
          onDeleted?.(docId);
        } else {
          error("Failed to delete file");
        }
      })
      .catch((error) => {
        console.error("Error deleting file:", error);
        error("An error occurred while deleting the file");
      });
  };

  return (
    <IconButton
      type="button"
      edge="end"
      aria-label="delete"
      onClick={() => handDelete(docId, reqUrl)}
    >
      <DeleteIcon color="error" />
    </IconButton>
  );
};
const DocumentActionIcon = ({ ...props }) => {
  const { docId, docType, docFileName, appId, onDeleted } = props;
  return (
    <>
      {/* <DownloadField
        docId={docId}
        docType={docType}
        docFileName={docFileName}
        appId={appId}
      /> */}
      {docType == "bbupload" || docType == "offline_closing" ? (
        <DocDeleteIcon
          docId={docId}
          docType={docType}
          appId={appId}
          onDeleted={onDeleted}
        />
      ) : (
        <> </>
      )}
    </>
  );
};
const UploadSingleFile = ({ ...props }) => {
  const appId = props?.appId;
  const uploadType = props?.uploadType;
  const fixedUploadName = props?.fixedUploadName;
  const useUploadFileName = props?.useUploadFileName;
  const uploadStep = props?.uploadStep;
  const requiredFileType = props?.requiredFileType;
  const uploadFieldId = props?.uploadFieldId;
  const uploadUrl = props?.uploadUrl;

  if (!appId) {
    return <div>Application ID is required</div>;
  }

  interface Item {
    id: string;
    name: string;
    filetype?: string;
  }

  const [items, setItems] = React.useState<Item[]>([]);
  const [uploadBtnStatus, setUploadBtnStatus] = React.useState(
    useUploadFileName ? true : fixedUploadName ? true : false
  );
  const [uploadingStatus, setUploadingStatus] = React.useState("");
  const { successNotify, errorNotify, infoNotify } = useNotify();

  const handleDeleteItem = React.useCallback((deletedId: string) => {
    setItems((prevItems) => prevItems.filter((item) => item.id !== deletedId));
    setUploadBtnStatus(true);
  }, []);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    let uploadFileName = document.getElementById(
      "uploadFileName"
    ) as HTMLInputElement;
    const file = e.target.files?.[0];
    if (!file) {
      return false;
    }
    let fileAize = file.size / 1024;
    if (fileAize > 1024) {
      errorNotify("File size should be less than 1MB");
      return false;
    }
    const fileNameType = file.name.split(".").pop();
    if (!fileNameType) {
      errorNotify("Invalid file type");
      return false;
    }
    let fileType = ["pdf", "jpg", "jpeg", "png", "doc", "docx", "xls", "xlsx"];
    if (requiredFileType && requiredFileType.length > 0) {
      fileType = requiredFileType;
    }
    if (fileType.indexOf(fileNameType) == -1) {
      if (requiredFileType) {
        errorNotify(
          "File type should be " + requiredFileType.join(", ") + " only"
        );
      } else {
        errorNotify(
          "File type should be pdf, jpg, jpeg, png, doc, docx, xls, xlsx"
        );
      }
      return false;
    }
    let fileName = file.name;
    //if uploadFileName have value, will use uploadFileName as file name
    if (useUploadFileName == true) {
      fileName = file.name;
    } else {
      if (fixedUploadName) {
        fileName = fixedUploadName + "." + fileNameType;
      } else {
        if (uploadFileName) {
          fileName = uploadFileName.value
            ? uploadFileName.value + "." + fileNameType
            : fileName;
        } else {
          errorNotify("Please enter new file name");
          return false;
        }
      }
    }
    if (file) {
      setUploadBtnStatus(true);
      setUploadingStatus("Uploading...");

      const reqUrl = `files`;
      const formData = new FormData();
      formData.append("file", file);
      formData.append("uploadType", uploadType);
      formData.append("uploadStep", uploadStep);
      formData.append("uploadFileName", fileName);
      //echo formData data
      const formJson = Object.fromEntries((formData as any).entries());

      //console.log("formData fileName===>",formJson.fileName);
      //console.log("formData file===>",formJson.file);
      const headers = await buildUploadHeaders();

      fetch(`${import.meta.env.VITE_API_URL}/files/upload/` + appId, {
        method: "POST",
        headers,
        body: formData,
      })
        .then((response) => response.json())
        .then((data) => {
          let fileId = data.fileId;
          let fileName = data.fileName;
          let newItem = { id: fileId, name: fileName, filetype: uploadType };
          setItems([...items, newItem]);
          setUploadBtnStatus(false);
          setUploadingStatus("");
          uploadFileName.value = "";
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };

  const CustomRemoveCircle = ({ ...props }) => {
    const { appId, fileId, fileName } = props;

    const handleRemove = async (appId: string, fileId: string) => {
      //remove file api
      const formData = new FormData();
      formData.append("fileId", fileId);
      formData.append("fileName", fileName);
      const headers = await buildUploadHeaders();

      fetch(`${import.meta.env.VITE_API_URL}/files/remove/` + appId, {
        method: "POST",
        headers,
        body: formData,
      })
        .then((response) => response.json())
        .then((data) => {
          successNotify("File removed successfully");
        })
        .catch((error) => {
          console.error(error);
        });

      //remove file UI
      if (items) {
        let newitems = items.filter((item) => item.id !== fileId);
        setItems(newitems);
      }
    };

    return (
      <IconButton
        aria-label="delete"
        onClick={() => handleRemove(appId, fileId)}
      >
        <DeleteIcon />
      </IconButton>
    );
  };

  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length > 0) {
      setUploadBtnStatus(true);
    } else {
      setUploadBtnStatus(false);
    }
  };

  return (
    <>
      <List sx={{ width: "100%", maxWidth: 360, bgcolor: "background.paper" }}>
        {items.map((item, index) => (
          <ListItem
            key={index}
            secondaryAction={
              <DocumentActionIcon
                docId={item.id}
                docType={item.filetype}
                docFileName={item.name}
                appId={appId}
                onDeleted={handleDeleteItem}
              />
            }
          >
            {/* <ListItemAvatar>
              <Avatar>
                <FileIconByExtension filename={item.name} />
              </Avatar>
            </ListItemAvatar> */}
            {/* secondary use today date */}
            <ListItemText primary={item.name} secondary={formatDate("today")} />
          </ListItem>
        ))}
      </List>
      {/* hidden filed, set item.id string use , implode */}
      <input
        type="hidden"
        name="uploadFileId"
        value={items.map((item) => item.id).join(",")}
      />
      {/* add file name filed */}
      {useUploadFileName ? (
        <></>
      ) : (
        <TextField
          id="uploadFileName"
          label="File Name"
          variant="outlined"
          value={fixedUploadName ? fixedUploadName : ""}
          onChange={handleFileNameChange}
          aria-readonly={fixedUploadName ? true : false}
          sx={fixedUploadName ? { background: "#ddd" } : {}}
        />
      )}

      <Button
        component="label"
        role={undefined}
        variant="contained"
        disabled={!uploadBtnStatus}
        tabIndex={-1}
        startIcon={<CloudUploadIcon />}
        sx={{ width: "100%", maxWidth: "500px", mt: 1 }}
      >
        <VisuallyHiddenInput
          type="file"
          id="file"
          name="file"
          onChange={handleFileChange}
        />
        {uploadingStatus}
      </Button>
    </>
  );
};

export default UploadSingleFile;
